// 📄 Fichier : Contracts.jsx

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaEye, FaEdit } from 'react-icons/fa';
import { QRCodeCanvas } from 'qrcode.react';
import '../styles/Contracts.css';
import MapSelector from '../components/MapSelector';
import MapCompteurSelector from '../components/MapCompteurSelector';
import { API } from '../config/api';

export default function Contracts() {
  const token = localStorage.getItem('token');
  const api = axios.create({
    baseURL: API+'/api/contracts',
    headers: { Authorization: `Bearer ${token}` }
  });

  const generateQr = () => {
    const d = new Date();
    return `QR-${d.getFullYear()}${d.getMonth() + 1}${d.getDate()}-${Math.floor(1000 + Math.random() * 9000)}`;
  };

  const getCurrentMonth = () => {
    const now = new Date();
    return now.toISOString().slice(0, 7); // Returns YYYY-MM format
  };

  const initialForm = () => ({
    codeQr: generateQr(),
    dateDebut: '',
    dateFin: '',
    numSerieCompteur: '',
    marqueCompteur: '',
    idClient: '',
    idS: '',
    posX: '',
    posY: '',
    // New fields for existing paper contracts
    hasExistingContract: false,
    lastPaymentPeriod: '',
    lastConsumptionReading: ''
  });

  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [search, setSearch] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(initialForm());
  const [editing, setEditing] = useState(null);
  const [showClientForm, setShowClientForm] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [newClient, setNewClient] = useState({
    nom: '', prenom: '', adresse: '', ville: '', tel: '', email: '', statut: 'actif'
  });





  useEffect(() => {
    fetchContracts();
    fetchClients();
    fetchSecteurs();
  }, []);

  const fetchContracts = async () => {
    try {
      const res = await api.get('/');
      // Ensure res.data is an array
      setContracts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur récupération contrats:', err);
      setContracts([]); // Set empty array on error
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(API+'/api/clients', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(res.data);
    } catch (err) {
      console.error('Erreur récupération clients:', err);
    }
  };

  const fetchSecteurs = async () => {
    try {
      const res = await axios.get(API+'/api/secteurs', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSecteurs(res.data);
    } catch (err) {
      console.error('Erreur récupération secteurs:', err);
    }
  };

  const openModal = (contract = null) => {
    if (contract) {
      setEditing(contract);
      setForm({
        codeQr: contract.codeqr,
        dateDebut: contract.datedebut?.split('T')[0],
        dateFin: contract.datefin?.split('T')[0] || '',
        numSerieCompteur: contract.numseriecompteur,
        marqueCompteur: contract.marquecompteur,
        idClient: contract.idclient,
        idS: contract.ids || '',
        posX: contract.posx,
        posY: contract.posy,
        // Don't show existing contract fields when editing
        hasExistingContract: false,
        lastPaymentPeriod: '',
        lastConsumptionReading: ''
      });
    } else {
      setEditing(null);
      setForm(initialForm());
    }
    setShowClientForm(false);
    setClientSearch('');
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for existing contract
    if (!editing && form.hasExistingContract) {
      if (!form.lastPaymentPeriod) {
        alert('⚠️ Veuillez entrer la période du dernier paiement');
        return;
      }
      if (!form.lastConsumptionReading || form.lastConsumptionReading <= 0) {
        alert('⚠️ Veuillez entrer une lecture de compteur valide');
        return;
      }
    }
    
    try {
      const user = JSON.parse(localStorage.getItem('user')) || {};
      const contractData = {
        ...form,
        dateFin: form.dateFin || null,
        idTech: user.idtech || null
      };
      
      let contractResponse;
      if (editing) {
        contractResponse = await api.put(`/${editing.idcontract}`, contractData);
      } else {
        contractResponse = await api.post('/', { ...contractData, dateContract: new Date().toISOString() });
      }
      
      // If this is a new contract with existing paper contract data
      if (!editing && form.hasExistingContract && form.lastPaymentPeriod && form.lastConsumptionReading) {
        try {
          // Get the contract ID from response - handle nested structure
          let contractId;
          
          if (contractResponse.data?.contract?.idcontract) {
            // Nested structure: { contract: { idcontract: 16 } }
            contractId = contractResponse.data.contract.idcontract;
          } else if (contractResponse.data?.idcontract) {
            // Direct structure: { idcontract: 16 }
            contractId = contractResponse.data.idcontract;
          } else if (contractResponse.data?.id) {
            // Alternative: { id: 16 }
            contractId = contractResponse.data.id;
          } else if (contractResponse.data?.insertId) {
            // MySQL insert result: { insertId: 16 }
            contractId = contractResponse.data.insertId;
          } else {
            // Last resort: maybe the response is just the ID
            contractId = contractResponse.data;
          }
          
          console.log('Contract response:', contractResponse.data);
          console.log('Extracted contract ID:', contractId);
          
          if (!contractId) {
            throw new Error('Contract ID not found in response');
          }
          
          // Create initial consumption record with correct field names
          const consumptionData = {
            idCont: contractId,                                    // Capitalized C (as expected by API)
            periode: form.lastPaymentPeriod,
            consommationPre: 0,                                   // Capitalized P (as expected by API)
            consommationActuelle: parseInt(form.lastConsumptionReading), // Capitalized A (as expected by API)
            jours: 30,
            idTech: user.idtech || null,                          // Capitalized T (as expected by API)
            status: 'fait'
          };
          
          console.log('Creating consumption with data:', consumptionData);
          
          await axios.post(API + '/api/consommations', consumptionData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          alert(`✅ Contrat créé avec succès!
📊 Consommation initiale enregistrée:
   • Période: ${form.lastPaymentPeriod}
   • Lecture: ${form.lastConsumptionReading} m³
   
🚀 Le suivi digital peut maintenant commencer à partir de ${form.lastPaymentPeriod}`);
        } catch (consError) {
          console.error('Erreur création consommation initiale:', consError);
          alert('✅ Contrat créé avec succès!\n⚠️ Erreur lors de la création de la consommation initiale. Vous pouvez l\'ajouter manuellement.');
        }
      } else if (!editing) {
        alert('✅ Contrat créé avec succès!\n📊 Une consommation initiale a été créée automatiquement.');
      }
      
      setModalOpen(false);
      setEditing(null);
      fetchContracts();
    } catch (err) {
      console.error('Erreur enregistrement contrat:', err);
      alert('❌ Erreur lors de l\'enregistrement du contrat');
    }
  };

  const handleClientSubmit = async () => {
    try {
      const res = await axios.post(API+'/api/clients', newClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchClients();
      setForm(f => ({ ...f, idClient: res.data.idclient }));
      setShowClientForm(false);
      setNewClient({ nom: '', prenom: '', adresse: '', ville: '', tel: '', email: '', statut: 'actif' });
    } catch (err) {
      console.error('Erreur ajout client:', err);
    }
  };

  const handleClientChange = (e) => {
    setNewClient({ ...newClient, [e.target.name]: e.target.value });
  };

  const handleSecteurChange = (e) => {
    const idS = e.target.value;
    setForm(f => ({ ...f, idS }));
    const secteur = secteurs.find(s => s.ids === parseInt(idS));
    if (secteur) {
      setForm(f => ({
        ...f,
        posX: secteur.longitude,
        posY: secteur.latitude
      }));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer ce contrat ?')) {
      try {
        await api.delete(`/${id}`);
        fetchContracts();
      } catch (err) {
        console.error('Erreur suppression contrat:', err);
      }
    }
  };

  const filtered = (Array.isArray(contracts) ? contracts : []).filter(c => {
    const query = search.toLowerCase();
    const searchMatch = (
      c.codeqr?.toLowerCase().includes(query) ||
      `${c.nomclient || ''} ${c.prenomclient || ''}`.toLowerCase().includes(query) ||
      (c.datedebut && new Date(c.datedebut).toLocaleDateString('fr-FR').includes(query))
    );
    
    const sectorMatch = filterSector === '' || c.ids === parseInt(filterSector);
    
    return searchMatch && sectorMatch;
  });

const isGoogleReady = typeof window !== 'undefined' && !!window.google?.maps;

if (!isGoogleReady) {
  return <div>Chargement de Google Maps...</div>;
}

  return (
    <div className="contract-page">
      <h1>Gestion des Contrats</h1>
      <div className="contract-toolbar">
        <input
          className="contract-filter-input"
          type="text"
          placeholder="Filtrer par client, contrat ou date..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="contract-filter-input"
          value={filterSector}
          onChange={e => setFilterSector(e.target.value)}
          style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minWidth: '150px' }}
        >
          <option value="">🏘️ Tous les secteurs</option>
          {secteurs.map(s => (
            <option key={s.ids} value={s.ids}>{s.nom}</option>
          ))}
        </select>
        <button className="contract-btn-primary" onClick={() => openModal()}>+ Ajouter Contrat</button>
      </div>

      <table className="contract-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Contrat</th>
            <th>Client</th>
            <th>Début</th>
            <th>N° Compteur</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((c, i) => (
            <tr key={i}>
              <td>{new Date(c.datecontract).toLocaleDateString('fr-FR')}</td>
              <td>{c.codeqr}</td>
              <td>{c.nomclient} {c.prenomclient}</td>
              <td>{new Date(c.datedebut).toLocaleDateString('fr-FR')}</td>
              <td>{c.marquecompteur ? `${c.marquecompteur}` : (c.numseriecompteur || '—')}</td>
              <td className="contract-actions">
                <button onClick={() => setViewing(c)}><FaEye /></button>
                <button onClick={() => openModal(c)}><FaEdit /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {modalOpen && (
        <div className="contract-modal">
          <form onSubmit={handleSubmit} className="contract-modal-content">
            <h2>{editing ? 'Modifier' : 'Nouveau'} Contrat</h2>
            <div className="form-grid">
              <div className="contract-form-section">
                <label>Code QR</label>
                <input value={form.codeQr} readOnly />
                <label>Numéro compteur</label>
                <input value={form.marqueCompteur} onChange={e => setForm({ ...form, marqueCompteur: e.target.value })} required />

                {/* Existing Contract Section */}
                {!editing && (
                  <div className="existing-contract-section" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                      <input
                        type="checkbox"
                        id="hasExistingContract"
                        checked={form.hasExistingContract}
                        onChange={e => setForm({ ...form, hasExistingContract: e.target.checked })}
                        style={{ marginRight: '10px' }}
                      />
                      <label htmlFor="hasExistingContract" style={{ fontWeight: 'bold', color: '#495057' }}>
                        📄 Client avec contrat papier existant
                      </label>
                    </div>
                    
                    {form.hasExistingContract && (
                      <div>
                        
                        <label>Période du dernier paiement *</label>
                        <input
                          type="month"
                          value={form.lastPaymentPeriod}
                          onChange={e => setForm({ ...form, lastPaymentPeriod: e.target.value })}
                          required={form.hasExistingContract}
                          style={{ marginBottom: '10px' }}
                          max={new Date().toISOString().slice(0, 7)} // Max current month
                        />
                        <br></br>
                        <label>Dernière lecture du compteur (m³) *</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={form.lastConsumptionReading}
                          onChange={e => setForm({ ...form, lastConsumptionReading: e.target.value })}
                          required={form.hasExistingContract}
                          placeholder="Ex: 1250"
                          style={{ marginBottom: '10px' }}
                        />
                        
                      </div>
                    )}
                  </div>
                )}

                <div className='contract-form-section-sec'>
                  <label>Secteur</label>
                <select value={form.idS} onChange={handleSecteurChange} required>
                  <option value="">Sélectionner un secteur</option>
                  {secteurs.map(s => (
                    <option key={s.ids} value={s.ids}>{s.nom}</option>
                  ))}
                </select>
                </div>

                <div className='contract-form-section-sec'>
                  <label>Client</label>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher un client par nom..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    style={{ marginBottom: '8px', padding: '8px', width: '100%', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                  {clientSearch && (
                    <div style={{ 
                      marginBottom: '8px', 
                      padding: '8px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '4px', 
                      fontSize: '13px',
                      maxHeight: '150px',
                      overflowY: 'auto',
                      border: '1px solid #e0e0e0'
                    }}>
                      <strong>Résultats ({clients.filter(c => {
                        const fullName = `${c.nom || ''} ${c.prenom || ''}`.toLowerCase().trim().replace(/\s+/g, ' ');
                        const searchTerm = clientSearch.toLowerCase().trim().replace(/\s+/g, ' ');
                        return fullName.includes(searchTerm);
                      }).length}):</strong>
                      {clients
                        .filter(c => {
                          const fullName = `${c.nom || ''} ${c.prenom || ''}`.toLowerCase().trim().replace(/\s+/g, ' ');
                          const searchTerm = clientSearch.toLowerCase().trim().replace(/\s+/g, ' ');
                          return fullName.includes(searchTerm);
                        })
                        .sort((a, b) => {
                          const nameA = `${a.nom} ${a.prenom}`.toLowerCase();
                          const nameB = `${b.nom} ${b.prenom}`.toLowerCase();
                          return nameA.localeCompare(nameB);
                        })
                        .slice(0, 10)
                        .map(c => (
                          <div 
                            key={c.idclient} 
                            onClick={() => {
                              setForm({ ...form, idClient: c.idclient });
                              setClientSearch('');
                              setShowClientForm(false);
                            }}
                            style={{ 
                              padding: '6px 8px',
                              marginTop: '4px',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              backgroundColor: form.idClient === c.idclient ? '#007bff' : 'transparent',
                              color: form.idClient === c.idclient ? 'white' : 'inherit',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (form.idClient !== c.idclient) {
                                e.currentTarget.style.backgroundColor = '#e9ecef';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (form.idClient !== c.idclient) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            • {c.nom} {c.prenom}
                          </div>
                        ))
                      }
                      {clients.filter(c => {
                        const fullName = `${c.nom || ''} ${c.prenom || ''}`.toLowerCase().trim().replace(/\s+/g, ' ');
                        const searchTerm = clientSearch.toLowerCase().trim().replace(/\s+/g, ' ');
                        return fullName.includes(searchTerm);
                      }).length > 10 && (
                        <div style={{ fontStyle: 'italic', color: '#666', marginTop: '8px', padding: '4px 8px' }}>
                          ... et {clients.filter(c => {
                            const fullName = `${c.nom || ''} ${c.prenom || ''}`.toLowerCase().trim().replace(/\s+/g, ' ');
                            const searchTerm = clientSearch.toLowerCase().trim().replace(/\s+/g, ' ');
                            return fullName.includes(searchTerm);
                          }).length - 10} autres (continuez à taper pour affiner)
                        </div>
                      )}
                    </div>
                  )}
                  <select
                    value={form.idClient}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'contract-add_new') {
                        setShowClientForm(true);
                        setForm({ ...form, idClient: '' }); 
                      } else {
                        setForm({ ...form, idClient: value });
                        setShowClientForm(false);
                      }
                    }}
                    required
                  >
                    <option value="">Sélectionner un client</option>
                    {clients
                      .filter(c => {
                        const fullName = `${c.nom || ''} ${c.prenom || ''}`.toLowerCase().trim().replace(/\s+/g, ' ');
                        const searchTerm = clientSearch.toLowerCase().trim().replace(/\s+/g, ' ');
                        return fullName.includes(searchTerm);
                      })
                      .sort((a, b) => {
                        const nameA = `${a.nom} ${a.prenom}`.toLowerCase();
                        const nameB = `${b.nom} ${b.prenom}`.toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map(c => (
                        <option key={c.idclient} value={c.idclient}>{c.nom} {c.prenom}</option>
                      ))
                    }
                    <option value="contract-add_new" >➕ Ajouter un nouveau client</option>
                  </select>

                </div>

                {showClientForm && (
                  <div className="contract-client-form">
                    <h4>Nouveau Client</h4>
                    <label >Nom</label>
                    <input name="nom"  value={newClient.nom} onChange={handleClientChange} required />
                    <label >Prenom</label>
                    <input name="prenom"  value={newClient.prenom} onChange={handleClientChange} required />
                    <label >Adresse</label>
                    <input name="adresse"  value={newClient.adresse} onChange={handleClientChange} required />
                    <label >Ville</label>
                    <input name="ville"  value={newClient.ville} onChange={handleClientChange} required />
                    <label >Telephone</label>
                    <input name="tel"  value={newClient.tel} onChange={handleClientChange} required />
                    <label >Email</label>
                    <input name="email" placeholder=" (optionnel)" value={newClient.email} onChange={handleClientChange} />
                    <label >Status</label>
                    <select name="statut" value={newClient.statut} onChange={handleClientChange}>
                      <option value="actif">Actif</option>
                      <option value="inactif">Inactif</option>
                    </select>
                    <button type="button" onClick={handleClientSubmit}>Créer le client</button>
                  </div>
                )}
              </div>

              <div className="contract-form-section">
                <label>Date de début</label>
                <input 
                  type="date" 
                  value={form.dateDebut} 
                  onChange={e => setForm({ ...form, dateDebut: e.target.value })} 
                  required
                />
                <label>Date de fin</label>
                <input 
                  type="date" 
                  value={form.dateFin} 
                  onChange={e => setForm({ ...form, dateFin: e.target.value })}
                />

                <label>Position du compteur</label>
               <MapCompteurSelector
                  secteurId={form.idS}
                  position={
                    form.posX && form.posY
                      ? { lat: parseFloat(form.posY), lng: parseFloat(form.posX) }
                      : null
                  }
                  onSelect={({ lat, lng }) =>
                    setForm(f => ({ ...f, posX: lng.toFixed(6), posY: lat.toFixed(6) }))
                  }
                />
              </div>
            </div>
            <div className="contract-modal-actions">
              <button type="submit">Enregistrer</button>
              <button type="button" onClick={() => setModalOpen(false)}>Annuler</button>
            </div>
          </form>
        </div>
      )}

      {viewing && (
        <div className="contract-modal">
          <div className="contract-modal-content">
            <h3>Détails du Contrat</h3>
            <div className='contract-modal-content-detaille'>
              <div>
                <h4 style={{ marginTop: 0, marginBottom: "10px", color: "#333" }}>📋 Code QR</h4>
                <p><b>Code QR:</b> {viewing.codeqr}</p>
                <QRCodeCanvas value={viewing.codeqr} size={120} />
              </div>
              <div>
                <p><b>Nom & Prénom:</b> {viewing.nomclient} {viewing.prenomclient}</p>
                
                <p><b>Date de début:</b> {new Date(viewing.datedebut).toLocaleDateString('fr-FR')}</p>
                <p><b>Date de fin:</b> {viewing.datefin ? new Date(viewing.datefin).toLocaleDateString('fr-FR') : 'Actif (sans date de fin)'}</p>
                <p><b>Date d'enregistrement:</b> {new Date(viewing.datecontract).toLocaleDateString('fr-FR')}</p>
                
                <p><b>Numéro compteur:</b> {viewing.marquecompteur || "—"}</p>
              </div>
            </div>
            <div className="contract-modal-actions">
              <a
                className="contract-btn-secondary"
                href={`https://api.qrserver.com/v1/create-qr-code/?data=${viewing.codeqr}&size=150x150`}
                download
                target="_blank"
                rel="noreferrer"
              >
                Télécharger QR
              </a>
              <button className="close-button" onClick={() => setViewing(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      <footer className="page-footer">
        <p>© 2025 Abar. Powered by <strong>Jysser Connect</strong></p>
      </footer>
    </div>
  );
}

